import "@/index.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import WhatsAppFab from "@/components/WhatsAppFab";
import ActiveTransactionBanner from "@/components/ActiveTransactionBanner";
import BottomNav from "@/components/BottomNav";
import ScrollControls from "@/components/ScrollControls";
import BackButton from "@/components/BackButton";
import Parrainage from "@/pages/Parrainage";
import MyFilleuls from "@/pages/MyFilleuls";
import Compte from "@/pages/Compte";
import Contacts from "@/pages/Contacts";
import Landing from "@/pages/Landing";
import SendMoney from "@/pages/SendMoney";
import Track from "@/pages/Track";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import MyTransactions from "@/pages/MyTransactions";
import MyRecentTransactions from "@/pages/MyRecentTransactions";
import FAQ from "@/pages/FAQ";
import MentionsLegales from "@/pages/MentionsLegales";
import Conditions from "@/pages/Conditions";
import AgentValidate from "@/pages/AgentValidate";
import AgentView from "@/pages/AgentView";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminTransactionDetail from "@/pages/AdminTransactionDetail";
import AdminSettings from "@/pages/AdminSettings";
import AdminLogs from "@/pages/AdminLogs";
import AdminAgents from "@/pages/AdminAgents";
import AdminReferrals from "@/pages/AdminReferrals";
import AdminReviews from "@/pages/AdminReviews";
import AdminUsers from "@/pages/AdminUsers";
import AdminAdmins from "@/pages/AdminAdmins";
import AdminContent from "@/pages/AdminContent";
import AdminFraud from "@/pages/AdminFraud";
import AdminMissions from "@/pages/AdminMissions";
import AdminVerifications from "@/pages/AdminVerifications";
import AdminPartners from "@/pages/AdminPartners";
import Partenaire from "@/pages/Partenaire";
import Templates from "@/pages/Templates";
import DashboardRedirect from "@/pages/DashboardRedirect";
import InstallPwaPrompt from "@/components/InstallPwaPrompt";
import PromoBanner from "@/components/PromoBanner";
import useStatusWatcher from "@/hooks/useStatusWatcher";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <PromoBanner />
          <ActiveTransactionBanner />
          <AppEffects />
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/envoyer" element={<SendMoney />} />
          <Route path="/envoyer/:etape" element={<SendMoney />} />
          <Route path="/dashboard" element={<DashboardRedirect />} />
          <Route path="/suivi" element={<Track />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/mes-transactions" element={<MyTransactions />} />
          <Route path="/mes-envois" element={<MyRecentTransactions />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/conditions" element={<Conditions />} />
          <Route path="/parrainage" element={<Parrainage />} />
          <Route path="/mes-filleuls" element={<MyFilleuls />} />
          <Route path="/partenaire" element={<Partenaire />} />
          <Route path="/compte" element={<Compte />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/modeles" element={<Templates />} />
          <Route path="/agent/valider" element={<AgentValidate />} />
          <Route path="/agent/voir" element={<AgentView />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="transactions/:id" element={<AdminTransactionDetail />} />
            <Route path="parametres" element={<AdminSettings />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="utilisateurs" element={<AdminUsers />} />
            <Route path="administrateurs" element={<AdminAdmins />} />
            <Route path="parrainage" element={<AdminReferrals />} />
            <Route path="missions" element={<AdminMissions />} />
            <Route path="verifications" element={<AdminVerifications />} />
            <Route path="partenaires" element={<AdminPartners />} />
            <Route path="avis" element={<AdminReviews />} />
            <Route path="contenu" element={<AdminContent />} />
            <Route path="fraude" element={<AdminFraud />} />
            <Route path="logs" element={<AdminLogs />} />
          </Route>
        </Routes>
        <FabSwitcher />
        <ScrollControls />
        <BottomNavSwitcher />
        <InstallPwaPrompt />
      </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

function BottomNavSwitcher() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  if (pathname.startsWith("/agent/")) return null;
  return <BottomNav />;
}

function AppEffects() {
  useStatusWatcher();
  const { pathname } = useLocation();
  const firstRender = useRef(true);
  // Capture a referral code from the URL (?ref=CODE) and persist it so attribution
  // survives navigation until registration (link format: https://.../?ref=CODE).
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) {
        const clean = ref.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20);
        if (clean) localStorage.setItem("sl_ref", clean);
      }
      const pe = new URLSearchParams(window.location.search).get("pe");
      if (pe) {
        const cleanPe = pe.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20);
        if (cleanPe) localStorage.setItem("sl_pe", cleanPe);
      }
    } catch (_e) { /* ignore */ }
  }, [pathname]);
  // Meta Pixel: track SPA route changes (initial PageView fired from index.html)
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  }, [pathname]);
  return null;
}

function FabSwitcher() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  if (pathname.startsWith("/agent/")) return null;
  return (
    <>
      <BackButton />
      <WhatsAppFab />
    </>
  );
}

export default App;
